import * as React from 'react'
import { useEffect } from 'react'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Theme, useTheme } from '@mui/material/styles'

import { ENTITY_TYPES, updateLoadingState } from '@sales-center/common'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { useAppDispatch } from '@user/store/hooks'

import { useFieldSettingsData } from './hooks/useFieldSettingsData'
import { CustomSettingsSection } from './CustomSettings/CustomSettingsSection'
import { FieldSettingsSection } from './FieldSettingsSection'

/**
 * Theme-derived styles for the per-entity wrapper.
 *
 * The Tidal layout is wider and gives its heading its own flex row; the legacy
 * one renders a bare heading with an explicit colour. Both states are resolved
 * here so the JSX only branches where the element tree genuinely differs.
 *
 * @param theme The active MUI theme.
 * @param isTidalThemeActive Whether the Tidal theme is active.
 */
const getStyles = (theme: Theme, isTidalThemeActive: boolean) => ({
  container: { maxWidth: isTidalThemeActive ? 766 : 700, flexGrow: 1 },

  tidalTitleRow: {
    my: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  legacyTitle: { color: theme.palette.text.primary, mb: 1, mt: 1.8 },
})

interface Props {
  entityType: ENTITY_TYPES
}

/**
 * Per-entity container that loads and renders one tab's field settings.
 *
 * Owns the data for its entity type: `useFieldSettingsData` fetches both the
 * built-in and custom field settings on mount and publishes the combined
 * loading state to the global loading indicator. Because the parent renders
 * only the active tab, this mounts (and therefore fetches) once per tab
 * selection.
 *
 * Renders the built-in fields through `FieldSettingsSection` and the custom
 * fields through `CustomSettingsSection`. Both read the Tidal theme flag
 * themselves. Theme-dependent styles come from `getStyles`; the heading keeps
 * its own branch because the Tidal layout wraps it in a flex row while the
 * legacy one renders it bare.
 *
 * @param entityType Which entity's field settings to load and display.
 */
export function FieldSettingsWrapper(props: Props) {
  const { entityType } = props
  const isTidalThemeActive = useTidalThemeActive()
  const theme = useTheme()
  const styles = getStyles(theme, isTidalThemeActive)
  const dispatch = useAppDispatch()

  const { settingsLoading, settings, settingsBySection, customSettings } =
    useFieldSettingsData(entityType)

  useEffect(() => {
    dispatch(updateLoadingState(settingsLoading ? 'visible' : 'hidden'))
  }, [dispatch, settingsLoading])

  const title =
    entityType === ENTITY_TYPES.Lead
      ? 'Lead Field Settings'
      : 'Opportunity Field Settings'

  return (
    <Box sx={styles.container}>
      {isTidalThemeActive ? (
        <Box sx={styles.tidalTitleRow}>
          <Typography variant="h4">{title}</Typography>
        </Box>
      ) : (
        <Typography variant="h5" sx={styles.legacyTitle}>
          {title}
        </Typography>
      )}
      <FieldSettingsSection
        settingsBySection={settingsBySection}
        entityType={entityType}
        settings={settings}
      />
      <CustomSettingsSection
        settings={customSettings}
        entityType={entityType}
      />
    </Box>
  )
}
FieldSettingsWrapper.displayName = 'FieldSettingsWrapper'
